import React, { useState, useMemo } from 'react';
import './MaterialSelector.css';

/**
 * MaterialSelector - A tabbed selector for top surface materials
 * Level 1: Material type tabs (Butcher Block, Laminate, ESD, Stainless, No Top)
 * Level 2: Depth filter within selected material
 * Level 3: Length options grid
 */
const MaterialSelector = ({ step, configuration, onSelect }) => {
    const selectedValue = configuration[step.key];

    // SVG icons for material types
    const NoTopIcon = () => (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="4" x2="20" y2="20" />
            <line x1="20" y1="4" x2="4" y2="20" />
        </svg>
    );

    const ButcherBlockIcon = () => (
        <svg viewBox="0 0 24 24" width="24" height="24">
            <rect x="2" y="4" width="20" height="16" rx="1" fill="#c9a66b" stroke="#8b6914" strokeWidth="1" />
            {/* Wood grain lines */}
            <line x1="5" y1="6" x2="5" y2="18" stroke="#a67c52" strokeWidth="0.5" />
            <line x1="8" y1="5" x2="8" y2="19" stroke="#a67c52" strokeWidth="0.5" />
            <line x1="11" y1="6" x2="11" y2="18" stroke="#a67c52" strokeWidth="0.5" />
            <line x1="14" y1="5" x2="14" y2="19" stroke="#a67c52" strokeWidth="0.5" />
            <line x1="17" y1="6" x2="17" y2="18" stroke="#a67c52" strokeWidth="0.5" />
            <line x1="20" y1="5" x2="20" y2="19" stroke="#a67c52" strokeWidth="0.5" />
            {/* Subtle grain curves */}
            <ellipse cx="6.5" cy="10" rx="0.8" ry="2" fill="none" stroke="#8b6914" strokeWidth="0.3" />
            <ellipse cx="12" cy="14" rx="0.8" ry="2.5" fill="none" stroke="#8b6914" strokeWidth="0.3" />
            <ellipse cx="18" cy="11" rx="0.6" ry="1.8" fill="none" stroke="#8b6914" strokeWidth="0.3" />
        </svg>
    );

    const Laminate125Icon = () => (
        <svg viewBox="0 0 24 24" width="24" height="24">
            {/* Thick laminate - layered appearance */}
            <rect x="2" y="6" width="20" height="12" rx="0.5" fill="#95a5a6" stroke="#7f8c8d" strokeWidth="1" />
            {/* Top layer */}
            <rect x="2" y="6" width="20" height="3" rx="0.5" fill="#bdc3c7" />
            {/* Bottom edge band */}
            <rect x="2" y="16" width="20" height="2" rx="0.5" fill="#7f8c8d" />
            {/* Bull nose edge indicator */}
            <path d="M22 8 Q24 12 22 16" fill="none" stroke="#7f8c8d" strokeWidth="1" />
        </svg>
    );

    const Laminate075Icon = () => (
        <svg viewBox="0 0 24 24" width="24" height="24">
            {/* Thin laminate */}
            <rect x="2" y="8" width="20" height="8" rx="0.3" fill="#95a5a6" stroke="#7f8c8d" strokeWidth="1" />
            {/* Top surface */}
            <rect x="2" y="8" width="20" height="2" rx="0.3" fill="#bdc3c7" />
            {/* PVC edge band - thin colored strip */}
            <rect x="2" y="15" width="20" height="1" fill="#5d6d7e" />
        </svg>
    );

    const EsdLaminateIcon = () => (
        <svg viewBox="0 0 24 24" width="24" height="24">
            {/* Base laminate surface */}
            <rect x="2" y="6" width="20" height="12" rx="0.5" fill="#34495e" stroke="#2c3e50" strokeWidth="1" />
            {/* ESD pattern - conductive grid */}
            <line x1="6" y1="6" x2="6" y2="18" stroke="#5d6d7e" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="12" y1="6" x2="12" y2="18" stroke="#5d6d7e" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="18" y1="6" x2="18" y2="18" stroke="#5d6d7e" strokeWidth="0.5" strokeDasharray="2,2" />
            {/* Lightning bolt symbol */}
            <path d="M14 7 L11 12 L13 12 L10 17 L14 11 L12 11 L14 7" fill="#f1c40f" stroke="#e67e22" strokeWidth="0.5" />
        </svg>
    );

    const StainlessSteelIcon = () => (
        <svg viewBox="0 0 24 24" width="24" height="24">
            {/* Brushed stainless steel surface */}
            <rect x="2" y="6" width="20" height="12" rx="0.5" fill="#d5d8dc" stroke="#aeb6bf" strokeWidth="1" />
            {/* Brushed metal texture lines */}
            <line x1="2" y1="8" x2="22" y2="8" stroke="#bdc3c7" strokeWidth="0.3" />
            <line x1="2" y1="10" x2="22" y2="10" stroke="#bdc3c7" strokeWidth="0.3" />
            <line x1="2" y1="12" x2="22" y2="12" stroke="#bdc3c7" strokeWidth="0.3" />
            <line x1="2" y1="14" x2="22" y2="14" stroke="#bdc3c7" strokeWidth="0.3" />
            <line x1="2" y1="16" x2="22" y2="16" stroke="#bdc3c7" strokeWidth="0.3" />
            {/* Highlight for metallic sheen */}
            <rect x="4" y="9" width="16" height="2" fill="rgba(255,255,255,0.3)" rx="0.5" />
        </svg>
    );

    // Material type definitions with metadata
    const materialTypes = useMemo(() => [
        {
            id: 'no-top',
            label: 'No Top',
            prefix: 'no-top',
            description: 'I will provide my own tabletop',
            icon: <NoTopIcon />
        },
        {
            id: 'butcher-block',
            label: 'Butcher Block',
            prefix: 'butcher-block',
            description: '1.75" thick maple with 1/8" eased edge',
            icon: <ButcherBlockIcon />
        },
        {
            id: 'laminate-125',
            label: 'Laminate 1.25"',
            prefix: 'laminate-125',
            description: 'Dove Grey with bull nose edge',
            icon: <Laminate125Icon />
        },
        {
            id: 'laminate-075',
            label: 'Laminate 0.75"',
            prefix: 'laminate-075',
            description: 'Dove Grey with .5MM PVC edge',
            icon: <Laminate075Icon />
        },
        {
            id: 'esd-laminate',
            label: 'ESD Laminate',
            prefix: 'esd-laminate',
            description: 'Static-dissipative surface for electronics',
            icon: <EsdLaminateIcon />
        },
        {
            id: 'stainless-steel',
            label: 'Stainless Steel',
            prefix: 'stainless-steel',
            description: 'Durable stainless steel surface',
            icon: <StainlessSteelIcon />
        }
    ], []);

    // Get frame depth and table size from configuration for filtering
    const frameDepth = configuration.frame_depth;
    const tableSize = configuration.table_size;
    
    // Determine allowed top depths based on frame depth
    // Rules: 22" frame -> 24" tops, 28" frame -> 30" or 36" tops
    const getAllowedTopDepths = () => {
        if (!frameDepth) return null; // No filtering if frame depth not set
        if (frameDepth === '22') return ['24'];
        if (frameDepth === '28') return ['30', '36'];
        return null;
    };
    
    // Check if 96" length tops are allowed (only for 84" length frames)
    const is96LengthAllowed = () => {
        if (!tableSize) return false;
        return tableSize.endsWith('x84') || tableSize.includes('x84');
    };

    // Parse options into material groups with depth/length structure
    const { optionsByMaterial, availableMaterials } = useMemo(() => {
        const byMaterial = {};
        const allowedDepths = getAllowedTopDepths();
        const allow96 = is96LengthAllowed();

        step.options.forEach(option => {
            // Determine material type from value prefix
            let materialId = null;
            for (const mat of materialTypes) {
                if (option.value.startsWith(mat.prefix)) {
                    materialId = mat.id;
                    break;
                }
            }

            if (!materialId) return;

            if (!byMaterial[materialId]) {
                byMaterial[materialId] = {
                    options: [],
                    depths: new Set(),
                    byDepth: {}
                };
            }

            // For "no-top", just add directly
            if (materialId === 'no-top') {
                byMaterial[materialId].options.push(option);
                return;
            }

            // Parse dimensions from label like "Butcher Block 24" x 30" x 1.75""
            const match = option.label.match(/(\d+)"?\s*x\s*(\d+)/);
            if (match) {
                const depth = match[1];
                const length = match[2];

                // Filter by allowed depths based on frame depth
                if (allowedDepths && !allowedDepths.includes(depth)) {
                    return; // Skip this option - depth not compatible with frame
                }

                // Filter 96" length tops - only allowed for 84" length frames
                if (length === '96' && !allow96) {
                    return; // Skip 96" tops unless frame is 84" length
                }

                byMaterial[materialId].depths.add(depth);
                if (!byMaterial[materialId].byDepth[depth]) {
                    byMaterial[materialId].byDepth[depth] = [];
                }
                byMaterial[materialId].byDepth[depth].push({
                    ...option,
                    depth,
                    length,
                    displayLength: `${length}"`
                });
            }

            byMaterial[materialId].options.push(option);
        });

        // Sort depths and lengths
        Object.keys(byMaterial).forEach(matId => {
            if (matId !== 'no-top') {
                const mat = byMaterial[matId];
                mat.depths = Array.from(mat.depths).sort((a, b) => parseInt(a) - parseInt(b));
                Object.keys(mat.byDepth).forEach(depth => {
                    mat.byDepth[depth].sort((a, b) => parseInt(a.length) - parseInt(b.length));
                });
            }
        });

        // Get list of available materials (that have options)
        const available = materialTypes.filter(mat =>
            byMaterial[mat.id] && byMaterial[mat.id].options.length > 0
        );

        return { optionsByMaterial: byMaterial, availableMaterials: available };
    }, [step.options, materialTypes, frameDepth, tableSize]);

    // Determine active material from current selection
    const getActiveMaterial = () => {
        if (selectedValue) {
            for (const mat of materialTypes) {
                if (selectedValue.startsWith(mat.prefix)) {
                    return mat.id;
                }
            }
        }
        return availableMaterials[0]?.id || null;
    };

    const [activeMaterial, setActiveMaterial] = useState(getActiveMaterial);
    const [activeDepth, setActiveDepth] = useState(null);

    // Update active depth when material changes
    React.useEffect(() => {
        const matData = optionsByMaterial[activeMaterial];
        if (matData && matData.depths && matData.depths.length > 0) {
            // Try to preserve depth if it exists in new material
            if (!activeDepth || !matData.depths.includes(activeDepth)) {
                setActiveDepth(matData.depths[0]);
            }
        } else {
            setActiveDepth(null);
        }
    }, [activeMaterial, optionsByMaterial, activeDepth]);

    const handleMaterialChange = (materialId) => {
        setActiveMaterial(materialId);
    };

    const handleDepthChange = (depth) => {
        setActiveDepth(depth);
    };

    const handleOptionSelect = (option) => {
        onSelect(step.key, option.value);
    };

    const currentMaterialData = optionsByMaterial[activeMaterial];
    const currentMaterialInfo = materialTypes.find(m => m.id === activeMaterial);

    if (availableMaterials.length === 0) {
        return (
            <div className="material-selector">
                <p className="step-description">{step.description}</p>
                <p className="no-options">No top surface options available.</p>
            </div>
        );
    }

    return (
        <div className="material-selector">
            <p className="step-description">{step.description}</p>

            {/* Material Type Tabs */}
            <div className="material-tabs-container">
                <label className="section-label">Material Type:</label>
                <div className="material-tabs">
                    {availableMaterials.map(mat => (
                        <button
                            key={mat.id}
                            type="button"
                            className={`material-tab ${activeMaterial === mat.id ? 'active' : ''}`}
                            onClick={() => handleMaterialChange(mat.id)}
                        >
                            <span className="material-icon">{mat.icon}</span>
                            <span className="material-name">{mat.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Material Description */}
            {currentMaterialInfo && (
                <div className="material-description">
                    {currentMaterialInfo.description}
                </div>
            )}

            {/* No Top - Simple selection */}
            {activeMaterial === 'no-top' && currentMaterialData && (
                <div className="no-top-option">
                    {currentMaterialData.options.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className={`no-top-button ${selectedValue === option.value ? 'selected' : ''}`}
                            onClick={() => handleOptionSelect(option)}
                        >
                            <span className="no-top-label">{option.label}</span>
                            <span className="no-top-price">Included</span>
                        </button>
                    ))}
                </div>
            )}

            {/* For materials with dimensions */}
            {activeMaterial !== 'no-top' && currentMaterialData && currentMaterialData.depths.length > 0 && (
                <>
                    {/* Depth Selector */}
                    <div className="depth-selector">
                        <label className="section-label">Depth:</label>
                        <div className="depth-options">
                            {currentMaterialData.depths.map(depth => (
                                <button
                                    key={depth}
                                    type="button"
                                    className={`depth-option ${activeDepth === depth ? 'active' : ''}`}
                                    onClick={() => handleDepthChange(depth)}
                                >
                                    {depth}"
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Length Grid */}
                    {activeDepth && currentMaterialData.byDepth[activeDepth] && (
                        <div className="length-selector">
                            <label className="section-label">Length:</label>
                            <div className="length-grid">
                                {currentMaterialData.byDepth[activeDepth].map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`length-option ${selectedValue === option.value ? 'selected' : ''}`}
                                        onClick={() => handleOptionSelect(option)}
                                    >
                                        <span className="length-value">{option.displayLength}</span>
                                        <span className="length-price">
                                            {option.priceModifier > 0
                                                ? `+$${option.priceModifier.toLocaleString()}`
                                                : 'Included'}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Selection Summary */}
            {selectedValue && (
                <div className="selection-summary">
                    <span className="summary-label">Selected:</span>
                    <span className="summary-value">
                        {step.options.find(o => o.value === selectedValue)?.label}
                    </span>
                </div>
            )}
        </div>
    );
};

export default MaterialSelector;
